import * as React from "react";
import Button from "@mui/material/Button";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import FormHelperText from "@mui/material/FormHelperText";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { MuiTelInput } from "mui-tel-input";
import BasicDatepicker from "../../BasicDatepicker";
import BasicSelect from "../../BasicSelect/index";
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
	useCreateBeneficiary,
	useGetProgramsByIds,
	useGetProvidersByProgramId,
	useGetProviderSitesByOUId,
	useReenrollBeneficiary,
} from "../../../hooks/useBeneficiaries/index";
import { useGetTreatmentPlans } from "../../../hooks/useTreatmentPlans";
import { useQueryClient } from "@tanstack/react-query";
import useDate from "../../../hooks/useDate";
import {
	BeneficiaryResponse,
	BeneficiaryPayload,
	Program,
} from "../../../domain/beneficiary.interface";
import { useEffect, useMemo, useState } from "react";
import {
	a11yProps,
	ACCOUNT_ID,
	generateSampleFromRegex,
	generateCINFromFormat,
	toBase64,
} from "../../../utils/general";
import { useFeedBack } from "../../../providers/FeedBackProvider/FeedBackContext";
import { ErrorResponse } from "../../../domain/error.interface";
import { useCreateAuditlog } from "../../../hooks/useAuditlog";
import { useTranslation } from "react-i18next";
import TabPanel from "../../TabPanel";
import createValidationSchema from "./validation.schema";
import { CustomDialog } from "../../styled/CustomDialog";
import { CustomStack } from "../../styled/CustomStack";
import { useNavigate } from "react-router-dom";
import TreatmentPlanFields from "../../TreatmentPlanFields";
import { Grid, Typography } from "@mui/material";
import { useAppTheme } from "../../../utils/theme";
import UploadButton from "../../UploadButton";
import { useUserStore } from "../../../stores/user.store";
import GenericAlertModal from "../GenericAlertModal";
import { compareFields } from "./compare.fields";
import { useSettings } from "../../../hooks/useSetting";
import { ISetting } from "@/domain/setting.interface";
import Spinner from "../../Spinner";
import { BENEFICIARY_STATUS } from "../../../domain/beneficiary.enum";

interface RegisterBeneficiaryModalProps {
	open: boolean;
	isRegister: boolean;
	onClose: () => void;
	beneficiary?: BeneficiaryResponse;
	onReenroll: () => void;
}

export default function RegisterBeneficiaryModal({
	open,
	isRegister,
	onClose,
	beneficiary,
	onReenroll,
}: RegisterBeneficiaryModalProps) {
	const { dayjs } = useDate();
	const { t } = useTranslation("common");
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { userInfo } = useUserStore();
	const theme = useAppTheme();
	const { showSnackBar: showError } = useFeedBack();

	const [selectedTab, setSelectedTab] = useState(0);
	const [openGenericDialog, setOpenGenericDialog] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const createBeneficiary = useCreateBeneficiary();
	const reenrollBeneficiary = useReenrollBeneficiary({
		beneficiaryId: beneficiary?._id || "",
		enrollmentId: beneficiary?.realLastEnrollment._id || "",
	});

	const {
		data: programs,
		isLoading: isLoadingPrograms,
		isFetching: isFetchingPrograms,
	} = useGetProgramsByIds(
		userInfo?.programs.map((program) => program.programId) || [],
	);

	const {
		data: treatmentPlans,
		isLoading: isLoadingTreatmentPlans,
		isFetching: isFetchingTreatmentPlans,
	} = useGetTreatmentPlans();

	const [ids, setIds] = useState({
		programId: "",
		providerId: "",
		providerSiteId: "",
	});
	const [programsOptions, setProgramsOptions] = useState<
		Array<{ key: string; value: string }>
	>([]);

	const [selectedProgram, setSelectedProgram] = useState<Program | undefined>();
	const [userProgram, setUserProgram] = useState<Program | undefined>();
	const {
		data: settings,
		isLoading: isLoadingSettings,
		isFetching: isFetchingSettings,
	} = useSettings(userInfo?.id);

	const {
		beneficiaryCINFormat,
		displayBeneficiaryAddress,
		displayBeneficiaryName,
		displayBeneficiaryDob,
		displayBeneficiaryCMEligible,
		autogeneratedCIN,
		minAgeValidation,
		medicaidIdFormat,
		gpraNumberFormat,
	} = settings || ({} as ISetting);

	const {
		data: providerSites,
		isLoading: isLoadingProviderSites,
		isFetching: isFetchingProviderSites,
		refetch: refetchProviderSites,
	} = useGetProviderSitesByOUId({
		ouIds: userProgram?.orgUnitId || [],
		queryKey: "sims-beneficiaries-register-provider-sites",
	});

	const {
		data: providersData,
		isLoading: isLoadingProviders,
		isFetching: isFetchingProviders,
		refetch: refetchProviders,
	} = useGetProvidersByProgramId(
		[selectedProgram?._id || ""],
		[ids.providerSiteId || ""],
	);

	const createAuditlogMutation = useCreateAuditlog();

	// Combine all loading states
	const isLoadingData = useMemo(
		() =>
			isLoadingPrograms ||
			isFetchingPrograms ||
			isLoadingTreatmentPlans ||
			isFetchingTreatmentPlans ||
			isLoadingSettings ||
			isFetchingSettings ||
			isLoadingProviderSites ||
			isFetchingProviderSites ||
			isLoadingProviders ||
			isFetchingProviders,
		[
			isLoadingPrograms,
			isFetchingPrograms,
			isLoadingTreatmentPlans,
			isFetchingTreatmentPlans,
			isLoadingSettings,
			isFetchingSettings,
			isLoadingProviderSites,
			isFetchingProviderSites,
			isLoadingProviders,
			isFetchingProviders,
		],
	);

	const handleChange = (event: React.SyntheticEvent, newValue: number) => {
		setSelectedTab(newValue);
	};

	const isNewEnrollment =
		status === BENEFICIARY_STATUS.COMPLETED ||
		dayjs(beneficiary?.realLastEnrollment?.endDate).isBefore(
			dayjs().subtract(30, "day"),
		);

	const isConsentFormRequired = selectedProgram?.parameters.consent
		? isRegister || isNewEnrollment || false
		: false;

	const schema = React.useMemo(() => {
		const validationOptions = {
			isEligibility: selectedProgram?.parameters.eligibility || false,
			beneficiaryCINFormat: (beneficiaryCINFormat as string) ?? "",
			displayBeneficiaryAddress: displayBeneficiaryAddress as boolean,
			displayBeneficiaryCMEligible: displayBeneficiaryCMEligible as boolean,
			displayBeneficiaryName: displayBeneficiaryName as boolean,
			isNewReenroll: isConsentFormRequired,
			minAgeValidation: minAgeValidation as number,
			displayBeneficiaryDob,
			medicaidIdFormat,
			gpraNumberFormat,
			autogeneratedCIN,
			isMultiTreatmentPlan: selectedProgram?.multiTreatmentPlan || false,
			isTreatmentPlanRequired: selectedProgram?.multiTreatmentPlan,
		};

		return createValidationSchema(t, validationOptions);
	}, [
		beneficiaryCINFormat,
		displayBeneficiaryAddress,
		displayBeneficiaryName,
		displayBeneficiaryDob,
		displayBeneficiaryCMEligible,
		isConsentFormRequired,
		selectedProgram,
		t,
	]);

	type BeneficiaryType = yup.InferType<typeof schema>;

	const {
		control,
		handleSubmit,
		reset,
		getValues,
		setValue,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(schema),
		mode: "all",
		defaultValues: {
			firstName: beneficiary?.beneficiary.firstName || "",
			lastName: beneficiary?.beneficiary.lastName || "",
			email: beneficiary?.beneficiary.email || "",
			phoneNumber: beneficiary?.beneficiary.phone[0].code
				? `${beneficiary?.beneficiary.phone[0].code} ${beneficiary?.beneficiary.phone[0].number}`
				: "",
			dateOfBirth: beneficiary?.beneficiary.dateOfBirth,
			addressLine1: beneficiary?.beneficiary.address.addressLine1 || "",
			addressLine2: beneficiary?.beneficiary.address.addressLine2 || "",
			city: beneficiary?.beneficiary.address.city || "",
			state: beneficiary?.beneficiary.address.state || "",
			zipCode: beneficiary?.beneficiary.address.zipCode || "",
			program: beneficiary?.realLastEnrollment.programId || "",
			providerSite: beneficiary?.realLastEnrollment.orgUnitId || "",
			provider: beneficiary?.realLastEnrollment.providerId || "",
			clientIDNumber: beneficiary?.beneficiary.sims.clientIdNumber || "",
			cmVisitSchedule: beneficiary?.beneficiary.sims.cmVisitSchedule || "",
			medicaidEligibility: beneficiary?.beneficiary.sims.medicaidEligibility
				? "true"
				: "false",
			cmEligibility: beneficiary?.beneficiary.sims.cmEligibility,
			// Treatment plan fields
			treatmentPlan: selectedProgram?.treatmentPlan || "",
			treatmentConfigTemplate: "",
			treatmentConfig: {
				visitsPerPeriod: "",
				minHoursBetweenVisits: "",
				duration: "",
				behavior: "",
				cmVisitSchedule: "",
			},
		},
	});

	useEffect(() => {
		if (
			isRegister &&
			displayBeneficiaryName &&
			displayBeneficiaryDob &&
			autogeneratedCIN &&
			beneficiaryCINFormat
		) {
			setValue(
				"clientIDNumber",
				generateCINFromFormat(beneficiaryCINFormat) as never,
			);
		}
	}, [
		isRegister,
		displayBeneficiaryName,
		displayBeneficiaryDob,
		autogeneratedCIN,
		beneficiaryCINFormat,
		setValue,
	]);

	const onCreateHandler = async (data: BeneficiaryPayload) => {
		const response = await createBeneficiary.mutateAsync(data);

		queryClient.invalidateQueries();

		navigate(`/beneficiaries/${response.beneficiaryId}`);
	};

	const onReenrollHandler = async (data: BeneficiaryPayload) => {
		const response = await reenrollBeneficiary.mutateAsync(data);

		queryClient.invalidateQueries();

		onReenroll();

		navigate(`/beneficiaries/${response.beneficiaryId}`);
	};

	const onSubmit = async (data: BeneficiaryType) => {
		setIsSubmitting(true);
		try {
			const { phoneNumber: phone } = data;
			const phoneParts = phone?.split(" ") ?? [];
			const phoneCode = phoneParts?.length > 1 ? phoneParts[0] : "";
			const phoneNumber =
				phoneParts?.length > 1 ? phoneParts.slice(1).join(" ") : "";
			const cleanedPhoneNumber = phoneNumber?.replace(/\s/g, "").trim();

			// @ts-expect-error - is type file
			const consentFile = data.file ? await toBase64(data.file) : "";
			// @ts-expect-error - is type file
			const fileType = data.file ? data.file.type : "";
			// @ts-expect-error - is type file
			const fileName = data.file ? data.file.name : "";
			const rawBase64 = consentFile.split(",")[1];

			// Helper function to get cmVisitSchedule value
			const getCmVisitScheduleValue = () => {
				// For multi-treatment plans, use template schedule data if available
				if (
					selectedProgram?.multiTreatmentPlan &&
					data.treatmentConfigTemplate &&
					data.treatmentConfigTemplate !== "custom"
				) {
					// Find the associated treatment plan
					const associatedTreatmentPlan = treatmentPlans?.find(
						(tp) => tp._id === selectedProgram.treatmentPlan,
					);

					if (
						associatedTreatmentPlan?.treatmentConfigOptions
							?.treatmentConfigTemplates
					) {
						const templates =
							associatedTreatmentPlan.treatmentConfigOptions
								.treatmentConfigTemplates;
						const templateIndex = parseInt(
							data.treatmentConfigTemplate.replace("template_", ""),
						);
						const template = templates[templateIndex];

						if (template?.cmVisitSchedule) {
							// Return the template's schedule data as a string
							const scheduleData = template.cmVisitSchedule as {
								cmVisitScheduleOptionsDays?: string;
								cmVisitScheduleOptionsDaysDisplay?: string;
							};
							return (
								scheduleData.cmVisitScheduleOptionsDays ||
								scheduleData.cmVisitScheduleOptionsDaysDisplay ||
								""
							);
						}
					}
				}

				// Fallback to legacy dropdown value
				return data.cmVisitSchedule || "";
			};

			const dataMapped: BeneficiaryPayload = {
				accountId: ACCOUNT_ID,
				firstName: data.firstName,
				lastName: data.lastName,
				email: data.email,
				phone: [
					{
						code: phoneCode || "",
						number: cleanedPhoneNumber || "",
					},
				],
				dateOfBirth: dayjs(data.dateOfBirth).toISOString(),
				address: {
					addressLine1: data.addressLine1,
					addressLine2: data.addressLine2,
					city: data.city,
					state: data.state,
					zipCode: data.zipCode,
				},
				sims: {
					clientIdNumber: data.clientIDNumber as string,
					medicaidEligibility: data.medicaidEligibility === "true",
					cmVisitSchedule: getCmVisitScheduleValue(),
					cmEligibility: data.cmEligibility,
				},
				userId: userInfo?.id as string,
				programId: data.program,
				orgUnitId: data.providerSite,
				providerId: data.provider,
				providerSite: data.providerSite,
				consentFile: rawBase64,
				fileType,
				fileName,
				// Include treatment plan fields if they exist
				...(selectedProgram?.multiTreatmentPlan && {
					treatmentPlan: selectedProgram.treatmentPlan,
					treatmentConfigTemplate: data.treatmentConfigTemplate,
					treatmentConfig: data.treatmentConfig
						? {
								...data.treatmentConfig,
								// Ensure behavior is a string, not an array
								// eslint-disable-next-line @typescript-eslint/no-explicit-any
								...((data.treatmentConfig as any).behavior && {
									behavior: Array.isArray(
										// eslint-disable-next-line @typescript-eslint/no-explicit-any
										(data.treatmentConfig as any).behavior,
									)
										? // eslint-disable-next-line @typescript-eslint/no-explicit-any
											(data.treatmentConfig as any).behavior[0]
										: // eslint-disable-next-line @typescript-eslint/no-explicit-any
											(data.treatmentConfig as any).behavior,
								}),
							}
						: data.treatmentConfig,
				}),
				isReenrol: !isRegister,
			};

			if (!isRegister) {
				const updatedFields = compareFields(
					dataMapped,
					beneficiary as BeneficiaryResponse,
					cleanedPhoneNumber,
				);
				const detail =
					updatedFields.length > 0
						? `Beneficiary's ${updatedFields.join(", ")} were updated by ${userInfo?.firstName} ${userInfo?.lastName}`
						: `Beneficiary were updated by ${userInfo?.firstName} ${userInfo?.lastName}`;

				await createAuditlogMutation.mutateAsync({
					appType: "WEB_BACK_OFFICE",
					module: "Beneficiary",
					action: "When re-enrolling a participant",
					detail: detail,
					actionCode: "WEB_PART_REENROLL",
					option: "Re-enroll",
					createdBy: userInfo?.id || "",
				});

				await onReenrollHandler(dataMapped);
			} else {
				await createAuditlogMutation.mutateAsync({
					appType: "WEB_BACK_OFFICE",
					module: "Beneficiary",
					action: "When creating a new beneficiary",
					detail: `Created new beneficiary ${data.firstName} ${data.lastName}`,
					actionCode: "WEB_PART_PAR_ADD",
					option: "Add",
					createdBy: userInfo?.id || "",
				});

				await onCreateHandler(dataMapped);
			}
			onClose();
		} catch (error) {
			showError(
				(error as ErrorResponse).error?.errorMessage ||
					(error as ErrorResponse).message?.[0] ||
					"An error occurred",
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	useEffect(() => {
		if (programs) {
			if (programs.length === 1) {
				setIds((ids) => ({ ...ids, programId: programs[0]._id }));
				setValue("program", programs[0]._id as never);
				setValue("treatmentPlan", (programs[0].treatmentPlan || "") as never);
				setSelectedProgram(programs[0]);
				setUserProgram(userInfo?.programs[0] as unknown as Program);
			}

			setProgramsOptions(
				programs.map((program) => ({
					key: program._id,
					value: program.name,
				})) || [],
			);
		}
	}, [programs]);

	useEffect(() => {
		if (providerSites) {
			if (providerSites.length === 1) {
				setIds((ids) => ({ ...ids, providerSiteId: providerSites[0].key }));
				setValue("providerSite", providerSites[0].key as never);
			}
		}
	}, [providerSites]);

	useEffect(() => {
		if (ids.providerSiteId) {
			refetchProviders();
		}
	}, [ids]);

	// Reset state when modal closes
	useEffect(() => {
		if (!open) {
			setIds({
				programId: "",
				providerId: "",
				providerSiteId: "",
			});

			setSelectedProgram(undefined);
			setUserProgram(undefined);
			queryClient.removeQueries({
				queryKey: ["sims-beneficiaries-register-provider-sites"],
			});
			queryClient.removeQueries({
				queryKey: ["sims-beneficiaries-providers"],
			});
		}
	}, [open]);

	useEffect(() => {
		// Wait for all required data to load before populating reenroll values
		// This prevents race conditions where duration shows as 0
		if (
			beneficiary &&
			programs &&
			!isLoadingTreatmentPlans &&
			!isFetchingTreatmentPlans
		) {
			setIds({
				programId: beneficiary.realLastEnrollment.programId || "",
				providerSiteId: beneficiary.realLastEnrollment.orgUnitId || "",
				providerId: beneficiary.realLastEnrollment.providerId || "",
			});

			const selectedProgramForBeneficiary = programs?.find(
				(program) => program._id === beneficiary.realLastEnrollment.programId,
			);

			setSelectedProgram(selectedProgramForBeneficiary);

			// Set treatmentPlan value for existing beneficiary
			if (selectedProgramForBeneficiary?.treatmentPlan) {
				setValue(
					"treatmentPlan",
					selectedProgramForBeneficiary.treatmentPlan as never,
				);
			}

			setUserProgram(
				userInfo?.programs?.find(
					(program) =>
						program.programId === beneficiary.realLastEnrollment.programId,
				) as unknown as Program,
			);
		}
	}, [
		beneficiary,
		programs,
		open,
		isLoadingTreatmentPlans,
		isFetchingTreatmentPlans,
	]);

	const onProgramChange = (selectedProgramId: string) => {
		queryClient.removeQueries({
			queryKey: ["sims-beneficiaries-register-provider-sites"],
		});
		queryClient.removeQueries({
			queryKey: ["sims-beneficiaries-providers"],
		});

		const values = getValues();
		const newSelectedProgram = programs?.find(
			(program) => program._id === selectedProgramId,
		);

		reset({
			...values,
			program: selectedProgramId,
			providerSite: "",
			provider: "",
			// Reset treatment plan fields when program changes
			treatmentPlan: newSelectedProgram?.treatmentPlan || "",
			treatmentConfigTemplate: "",
			treatmentConfig: {
				visitsPerPeriod: "",
				minHoursBetweenVisits: "",
				duration: "",
				behavior: "",
				cmVisitSchedule: "",
			},
		});

		setIds({
			programId: selectedProgramId,
			providerId: "",
			providerSiteId: "",
		});

		setSelectedProgram(newSelectedProgram);

		setUserProgram(
			userInfo?.programs?.find(
				(program) => program.programId === selectedProgramId,
			) as unknown as Program,
		);
	};

	useEffect(() => {
		refetchProviderSites();
	}, [userProgram]);

	const isDisabledProviderSite = React.useMemo(() => {
		if (isRegister) {
			if (isLoadingProviderSites) {
				return true;
			} else if (providerSites?.length === 1) {
				return true;
			} else {
				return false;
			}
		} else if (beneficiary?.realLastEnrollment.isNewEnrollment) {
			if (providerSites?.length === 1) {
				return true;
			}

			return false;
		} else {
			if (providerSites?.length === 1) {
				return true;
			}
			return false;
		}
	}, [isRegister, beneficiary, isLoadingProviderSites]);

	const isDisableConsentForm = React.useMemo(() => {
		if (isRegister) {
			return false;
		} else if (beneficiary?.realLastEnrollment.isNewEnrollment) {
			return false;
		} else {
			return true;
		}
	}, [isRegister, beneficiary]);

	let beneficiaryCINFormatSample = "";
	if (beneficiaryCINFormat) {
		beneficiaryCINFormatSample = generateSampleFromRegex(
			beneficiaryCINFormat as string,
		);
	}

	const providers = useMemo(
		() =>
			providersData
				? providersData.filter((p) =>
						p.roles.some((r) => selectedProgram?.cmRoles?.includes(r)),
					)
				: [],
		[providersData, selectedProgram],
	);

	return (
		<React.Fragment>
			<CustomDialog
				onClose={onClose}
				aria-labelledby="register-beneficiary-dialog-title"
				sx={{
					"& .MuiDialog-paper": {
						width: "100%",
						maxWidth: 760,
						minHeight: "calc(100vh - 64px)",
					},
				}}
				open={open}
			>
				<DialogTitle
					sx={{ m: 0, p: 2 }}
					id="register-beneficiary-dialog-title"
					variant="headlineSmall"
					color={(theme) => theme.palette.surface.light}
					bgcolor={(theme) => theme.palette.primary.dark}
				>
					{!isRegister ? "Re-enroll Beneficiary" : "Register Beneficiary"}
				</DialogTitle>
				<IconButton
					aria-label="close"
					onClick={onClose}
					sx={{
						position: "absolute",
						right: 8,
						top: 8,
						color: (theme) => theme.palette.surface.light,
					}}
				>
					<CloseIcon />
				</IconButton>
				<DialogContent
					sx={{
						gap: "1rem",
						display: "flex",
						flexDirection: "column",
						minHeight: "400px",
						justifyContent:
							isLoadingData || isSubmitting ? "center" : "flex-start",
						alignItems: isLoadingData || isSubmitting ? "center" : "stretch",
					}}
				>
					{isLoadingData || isSubmitting ? (
						<Spinner />
					) : (
						<Box sx={{ width: "100%" }}>
							<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
								<Tabs
									value={selectedTab}
									onChange={handleChange}
									aria-label="basic tabs example"
								>
									<Tab label="General information" {...a11yProps(0)} />
									<Tab label="SIMS INFORMATION" {...a11yProps(1)} />
								</Tabs>
							</Box>
							<TabPanel value={selectedTab} index={0}>
								{(displayBeneficiaryName as boolean) && (
									<CustomStack direction="row">
										<Controller
											name="firstName"
											control={control}
											defaultValue=""
											render={({ field }) => (
												<TextField
													{...field}
													id="firstName"
													label="First name"
													variant="outlined"
													required
													value={field.value}
													onChange={(value) => field.onChange(value)}
													error={!!errors.firstName}
													helperText={
														errors.firstName ? errors.firstName.message : null
													}
												/>
											)}
										/>
										<Controller
											name="lastName"
											control={control}
											defaultValue=""
											render={({ field }) => (
												<TextField
													{...field}
													id="lastName"
													label="Last name"
													variant="outlined"
													required
													value={field.value}
													onChange={(value) => field.onChange(value)}
													error={!!errors.lastName}
													helperText={
														errors.lastName ? errors.lastName.message : null
													}
												/>
											)}
										/>
									</CustomStack>
								)}
								<CustomStack direction="row">
									<Controller
										name="email"
										control={control}
										defaultValue=""
										render={({ field }) => (
											<TextField
												{...field}
												id="email"
												label="Email"
												variant="outlined"
												value={field.value}
												onChange={(value) => field.onChange(value)}
												error={!!errors.email}
												helperText={errors.email ? errors.email.message : null}
											/>
										)}
									/>
									<Controller
										name="phoneNumber"
										control={control}
										defaultValue=""
										render={({ field }) => (
											<MuiTelInput
												{...field}
												defaultCountry="US"
												disableDropdown
												label="Phone number"
												variant="outlined"
												className="flex flex-1"
												value={field.value}
												onChange={(value) => field.onChange(value)}
												error={!!errors.phoneNumber}
												helperText={
													errors.phoneNumber ? errors.phoneNumber.message : null
												}
											/>
										)}
									/>
								</CustomStack>
								<CustomStack direction="row">
									{displayBeneficiaryDob ? (
										<Controller
											name="dateOfBirth"
											control={control}
											render={({ field }) => (
												<div>
													<BasicDatepicker
														{...field}
														label="Date of birth (mm/dd/yyyy)*"
														onChange={(value) => field.onChange(value)}
														value={field.value ? dayjs(field.value) : null}
													/>
													{errors.dateOfBirth && (
														<FormHelperText className="!ml-[14px]" error>
															{errors.dateOfBirth.message}
														</FormHelperText>
													)}
												</div>
											)}
										/>
									) : (
										<></>
									)}
								</CustomStack>
								{(displayBeneficiaryAddress as boolean) && (
									<>
										<Stack spacing={{ xs: 1, sm: 2 }}>
											<Controller
												name="addressLine1"
												control={control}
												defaultValue=""
												render={({ field }) => (
													<TextField
														{...field}
														id="addressLine1"
														label="Address line 1"
														variant="outlined"
														className="flex flex-1"
														value={field.value}
														onChange={(value) => field.onChange(value)}
														error={!!errors.addressLine1}
														helperText={
															errors.addressLine1
																? errors.addressLine1.message
																: null
														}
													/>
												)}
											/>
											<Controller
												name="addressLine2"
												control={control}
												defaultValue=""
												render={({ field }) => (
													<TextField
														{...field}
														id="addressLine2"
														label="Address line 2"
														variant="outlined"
														className="flex flex-1"
														value={field.value}
														onChange={(value) => field.onChange(value)}
														error={!!errors.addressLine2}
														helperText={
															errors.addressLine2
																? errors.addressLine2.message
																: null
														}
													/>
												)}
											/>
										</Stack>
										<CustomStack direction="row">
											<Controller
												name="city"
												control={control}
												defaultValue=""
												render={({ field }) => (
													<TextField
														{...field}
														id="city"
														label="City"
														variant="outlined"
														className="flex flex-1"
														value={field.value}
														onChange={(value) => field.onChange(value)}
														error={!!errors.city}
														helperText={
															errors.city ? errors.city.message : null
														}
													/>
												)}
											/>
											<Controller
												name="state"
												control={control}
												defaultValue=""
												render={({ field }) => (
													<TextField
														{...field}
														id="state"
														label="State"
														variant="outlined"
														className="flex flex-1"
														value={field.value}
														onChange={(value) => field.onChange(value)}
														error={!!errors.state}
														helperText={
															errors.state ? errors.state.message : null
														}
													/>
												)}
											/>
										</CustomStack>
									</>
								)}

								{settings?.displayBeneficiaryZipcode && (
									<CustomStack direction="row">
										<Controller
											name="zipCode"
											control={control}
											defaultValue=""
											render={({ field }) => (
												<TextField
													{...field}
													id="zipCode"
													label="ZIP Code"
													variant="outlined"
													className="flex flex-1"
													value={field.value}
													onChange={(value) => field.onChange(value)}
													error={!!errors.zipCode}
													helperText={
														errors.zipCode ? errors.zipCode.message : null
													}
												/>
											)}
										/>
									</CustomStack>
								)}
							</TabPanel>
							<TabPanel value={selectedTab} index={1}>
								<CustomStack direction="row">
									<Controller
										name="program"
										control={control}
										defaultValue={ids.programId ?? undefined}
										render={({ field }) => (
											<BasicSelect
												{...field}
												data={programsOptions || []}
												value={field.value}
												required
												label="Program"
												id="beneficiary-program"
												handleChange={(value) => {
													const selectedProgramId = value as string;
													field.onChange(selectedProgramId);

													onProgramChange(selectedProgramId);
												}}
												error={!!errors.program}
												errorMessage={errors.program?.message || ""}
												disabled={isLoadingPrograms}
											/>
										)}
									/>

									{/* Treatment Plan Fields - conditionally shown for multi-treatment plan programs */}
									{/* Only render when treatment plans are fully loaded to prevent race conditions */}
									{!isLoadingTreatmentPlans && !isFetchingTreatmentPlans && (
										<TreatmentPlanFields
											control={control}
											errors={errors}
											selectedProgram={selectedProgram}
											setValue={setValue}
											getValues={getValues}
											isEditMode={false}
											isReenrollMode={!isRegister}
											currentTreatmentPlan={
												!isRegister &&
												beneficiary?.realLastEnrollment?.currentTreatmentPlan
													? beneficiary.realLastEnrollment.currentTreatmentPlan
													: undefined
											}
											enrollment={beneficiary?.realLastEnrollment}
										/>
									)}

									<Controller
										key={`provider-site-ctrl-${ids.programId}`}
										name="providerSite"
										control={control}
										defaultValue={
											providerSites?.length === 1
												? providerSites[0].key
												: ids.providerSiteId
										}
										render={({ field }) => (
											<BasicSelect
												{...field}
												data={providerSites || []}
												value={field.value}
												required
												label="Provider site"
												id="beneficiary-provider-site"
												handleChange={(value) => {
													field.onChange(value);
													setIds((ids) => ({
														...ids,
														providerSiteId: value as string,
													}));
												}}
												error={!!errors.providerSite}
												errorMessage={errors.providerSite?.message || ""}
												disabled={
													ids.programId === "" || isDisabledProviderSite
												}
											/>
										)}
									/>
								</CustomStack>
								<CustomStack direction="row">
									<Controller
										name="provider"
										control={control}
										defaultValue={
											providers?.length === 1
												? providers[0].key
												: ids.providerId
										}
										render={({ field }) => (
											<BasicSelect
												{...field}
												data={providers || []}
												value={field.value}
												required
												label="Provider"
												id="beneficiary-provider"
												handleChange={(value) => field.onChange(value)}
												error={!!errors.provider}
												errorMessage={errors.provider?.message || ""}
												disabled={
													ids.providerSiteId === "" || isLoadingProviders
												}
											/>
										)}
									/>
									<Controller
										name="clientIDNumber"
										control={control}
										defaultValue=""
										render={({ field }) => (
											<TextField
												{...field}
												id="state"
												label="Client ID Number (CIN)"
												disabled={
													displayBeneficiaryName &&
													displayBeneficiaryDob &&
													autogeneratedCIN
												}
												required={
													!(
														displayBeneficiaryName &&
														displayBeneficiaryDob &&
														autogeneratedCIN
													)
												}
												variant="outlined"
												className="flex flex-1"
												value={field.value}
												onChange={(value) => field.onChange(value)}
												error={!!errors.clientIDNumber}
												placeholder={beneficiaryCINFormatSample ?? undefined}
												helperText={
													errors.clientIDNumber
														? errors.clientIDNumber.message
														: null
												}
											/>
										)}
									/>
								</CustomStack>
								<Grid
									container
									rowSpacing={2}
									columnSpacing={1}
									style={{ marginTop: "0px" }}
								>
									{/* Legacy CM Visit Schedule - only show when program doesn't have treatment plan */}
									{!selectedProgram?.multiTreatmentPlan && (
										<Controller
											name="cmVisitSchedule"
											control={control}
											defaultValue=""
											render={({ field }) => (
												<Grid item xs={6}>
													<BasicSelect
														{...field}
														data={[
															{
																key: "MondayThursday",
																value: "Monday and Thursday",
															},
															{
																key: "TuesdayFriday",
																value: "Tuesday and Friday",
															},
														]}
														value={field.value}
														required
														label="CM visit schedule"
														id="beneficiary-program"
														handleChange={(value) => field.onChange(value)}
														error={!!errors.cmVisitSchedule}
														errorMessage={errors.cmVisitSchedule?.message ?? ""}
													/>
												</Grid>
											)}
										/>
									)}
									{selectedProgram?.parameters.eligibility && (
										<Controller
											name="medicaidEligibility"
											control={control}
											defaultValue="false"
											render={({ field }) => (
												<Grid item xs={6}>
													<BasicSelect
														{...field}
														data={[
															{ key: "true", value: "Yes" },
															{ key: "false", value: "No" },
														]}
														value={field.value}
														required
														label="Medicaid eligibility"
														id="beneficiary-medicaid-eligibility"
														handleChange={(value) => field.onChange(value)}
													/>
												</Grid>
											)}
										/>
									)}
									<>
										{displayBeneficiaryCMEligible && (
											<Controller
												name="cmEligibility"
												control={control}
												defaultValue={false}
												render={({ field }) => (
													<Grid item xs={6} className="mt-3">
														<BasicSelect
															{...field}
															data={[
																{ key: "true", value: "Yes" },
																{ key: "false", value: "No" },
															]}
															value={field.value ? "true" : "false"}
															required
															label="CM Eligible"
															id="beneficiary-cm-eligibility"
															handleChange={(value) =>
																field.onChange(value === "true" ? true : false)
															}
															error={!!errors.cmEligibility}
															errorMessage={errors.cmEligibility?.message ?? ""}
														/>
													</Grid>
												)}
											/>
										)}
									</>
								</Grid>

								<Box sx={{ py: 2 }}>
									<Typography
										variant="titleSmall"
										color={theme.palette.accent.main}
									>
										Consent form
									</Typography>

									<Box sx={{ py: 2 }}>
										<Controller
											name="file"
											control={control}
											defaultValue={undefined}
											render={({ field }) => (
												<>
													<UploadButton
														onChange={(value) => field.onChange(value)}
														value={field.value as File}
														disabled={
															createBeneficiary.isPending ||
															reenrollBeneficiary.isPending ||
															isDisableConsentForm
														}
													/>
													{errors.file && (
														<FormHelperText className="!ml-[14px]" error>
															{errors.file?.message}
														</FormHelperText>
													)}
												</>
											)}
										/>
									</Box>
								</Box>
							</TabPanel>
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<Button size="large" onClick={onClose} variant="outlined">
						CANCEL
					</Button>
					<Button
						size="large"
						onClick={async () => {
							const totalAmountEarnedByCurrentYear =
								beneficiary?.totalAmountEarnedByCurrentYear || 0;
							const maxYearlyEarnings = beneficiary?.realLastEnrollment?.program
								?.parameters?.maxYearlyEarnings as number;

							if (
								!isRegister &&
								totalAmountEarnedByCurrentYear >= maxYearlyEarnings
							) {
								setOpenGenericDialog(true);
								return;
							}

							handleSubmit(onSubmit)();
						}}
						color="primary"
						variant="contained"
						sx={{ textTransform: "none", textAlign: "center" }}
						disabled={
							createBeneficiary.isPending ||
							reenrollBeneficiary.isPending ||
							createAuditlogMutation.isPending ||
							isSubmitting
						}
					>
						{!isRegister ? "REENROLL" : "REGISTER"}
					</Button>
				</DialogActions>
			</CustomDialog>

			{openGenericDialog ? (
				<GenericAlertModal
					title="Error"
					description={`Beneficiary earned the maximum amount of Incentives this year and is no longer eligible to receive CM Services or re-enroll until the 1st of January of ${dayjs().add(1, "year").year()}.`}
					submitText="Accept"
					onClick={() => setOpenGenericDialog(false)}
				/>
			) : null}
		</React.Fragment>
	);
}
